"use client";

import { useActionState, useState } from "react";
import { affiliateSignupAction } from "@/lib/affiliates/affiliate-actions";
import { type AffiliateSignupState } from "@/lib/affiliates/affiliate-constants";
import { useActionToast } from "@/components/ui/toast";

const inputBase =
  "w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface)] px-3.5 py-2.5 text-sm text-[var(--foreground)] outline-none focus:border-[rgba(94,207,255,0.5)] focus:ring-2 focus:ring-[var(--ring)]";
const labelBase = "block text-sm font-semibold text-[var(--foreground)]";
const checkRow = "flex items-start gap-3 text-sm text-[var(--muted-foreground)]";

export function AffiliateSignupForm() {
  const [state, formAction, pending] = useActionState(affiliateSignupAction, null as AffiliateSignupState);
  useActionToast(state);
  const [copied, setCopied] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState("bank_transfer");

  // Success with a share link — show it and a copy button instead of the form.
  if (state?.shareUrl) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-sm text-emerald-300">{state.ok}</p>
        <div className="rounded-md border border-[var(--border-strong)] bg-[var(--surface)] p-3">
          <p className="break-all font-mono text-xs text-[var(--foreground)]">{state.shareUrl}</p>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(state.shareUrl!);
              setCopied(true);
            } catch {
              /* clipboard may be blocked — the link is visible above to copy by hand */
            }
          }}
          className="w-full rounded-md bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[var(--accent-foreground)] transition hover:opacity-90"
        >
          {copied ? "Copied!" : "Copy my link"}
        </button>
        <p className="text-xs text-[var(--muted-foreground)]">
          Share this with anyone who might need us. If they become a customer, you earn, with no catch.
        </p>
      </div>
    );
  }

  return (
    <form action={formAction} className="space-y-4">
      <div className="space-y-1.5">
        <label htmlFor="fullName" className={labelBase + " after:ml-1 after:text-amber-300 after:content-['*']"}>
          Your name
        </label>
        <input id="fullName" name="fullName" type="text" required maxLength={200} className={inputBase} />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="email" className={labelBase + " after:ml-1 after:text-amber-300 after:content-['*']"}>
          Email
        </label>
        <input id="email" name="email" type="email" required maxLength={320} className={inputBase} />
      </div>
      {/* ONE BOX PER THING THE BANK ASKS FOR.
          This was a single free-text "Payment details" field taking a sort code, an account number
          and a name in whatever order somebody typed them. That is the field a payment fails on:
          nothing checks the sort code is six digits or the account is eight, so the first anyone
          knows is a rejected transfer weeks later, to a person who is owed money and now thinks we
          are messing them about. The pattern attributes below are the same check the bank does. */}
      <div className="space-y-1.5">
        <label htmlFor="payoutMethod" className={labelBase}>
          How should we pay you?
        </label>
        <select
          id="payoutMethod"
          name="payoutMethod"
          value={payoutMethod}
          onChange={(e) => setPayoutMethod(e.target.value)}
          className={inputBase}
        >
          <option value="bank_transfer">Bank transfer</option>
          <option value="paypal">PayPal</option>
        </select>
      </div>

      {payoutMethod === "bank_transfer" ? (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="accountName" className={labelBase + " after:ml-1 after:text-amber-300 after:content-['*']"}>
              Name on the account
            </label>
            <input
              id="accountName"
              name="accountName"
              type="text"
              required
              maxLength={200}
              autoComplete="off"
              placeholder="As it appears on your statement"
              className={inputBase}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="sortCode" className={labelBase + " after:ml-1 after:text-amber-300 after:content-['*']"}>
                Sort code
              </label>
              <input
                id="sortCode"
                name="sortCode"
                type="text"
                inputMode="numeric"
                required
                maxLength={8}
                autoComplete="off"
                placeholder="00-00-00"
                pattern="[0-9\\s-]{6,8}"
                title="Six digits, with or without dashes."
                className={inputBase}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="accountNumber" className={labelBase + " after:ml-1 after:text-amber-300 after:content-['*']"}>
                Account number
              </label>
              <input
                id="accountNumber"
                name="accountNumber"
                type="text"
                inputMode="numeric"
                required
                maxLength={10}
                autoComplete="off"
                placeholder="00000000"
                pattern="[0-9\\s]{8,10}"
                title="Eight digits."
                className={inputBase}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          <label htmlFor="paypalEmail" className={labelBase + " after:ml-1 after:text-amber-300 after:content-['*']"}>
            PayPal email
          </label>
          <input
            id="paypalEmail"
            name="paypalEmail"
            type="email"
            required
            maxLength={320}
            autoComplete="off"
            placeholder="you@example.com"
            className={inputBase}
          />
        </div>
      )}

      <div className="space-y-3 rounded-md border border-[var(--border)] bg-[var(--surface)]/60 p-4">
        <label htmlFor="acceptTerms" className={checkRow}>
          <input id="acceptTerms" name="acceptTerms" type="checkbox" required className="mt-0.5 size-4 shrink-0" />
          <span>
            I&apos;ve read and agree to the{" "}
            <a href="/affiliates/terms" target="_blank" className="text-[var(--accent)] underline">
              affiliate terms
            </a>
            . I&apos;ll only share my link honestly, with no spam or misleading claims.
          </span>
        </label>
        <label htmlFor="acceptTax" className={checkRow}>
          <input id="acceptTax" name="acceptTax" type="checkbox" required className="mt-0.5 size-4 shrink-0" />
          <span>
            I understand I&apos;m responsible for declaring anything I earn to HMRC; Ownly doesn&apos;t deduct tax.
          </span>
        </label>
      </div>

      {state?.error ? <p className="text-sm text-red-300/90">{state.error}</p> : null}

      <button
        type="submit"
        disabled={pending}
        className="w-full rounded-md bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[var(--accent-foreground)] transition hover:opacity-90 disabled:opacity-60"
      >
        {pending ? "Setting you up…" : "Get my referral link"}
      </button>
    </form>
  );
}
